
import { supabase } from "@/integrations/supabase/client";
import { SessionState } from "./SessionStateTypes";

export class AuthStateManager {
  private static instance: AuthStateManager | null = null;
  private isDestroyed: boolean = false;
  private currentState: SessionState = 'initializing';
  private refreshInterval: NodeJS.Timeout | null = null;
  private isBillboardMode: boolean = false;
  private retryAttempts: number = 0;
  private maxRetries: number = 5;
  private lastRefreshTime: number = 0;

  private constructor() {
    this.isBillboardMode = localStorage.getItem('billboard_mode') === 'true';
  }

  public static getInstance(): AuthStateManager {
    if (!AuthStateManager.instance || AuthStateManager.instance.isDestroyed) {
      AuthStateManager.instance = new AuthStateManager();
    }
    return AuthStateManager.instance;
  }

  // Removed setupVisibilityListener - handled by supabase client

  public setBillboardMode(enabled: boolean) {
    console.log('[AuthStateManager] Setting billboard mode:', enabled);
    this.isBillboardMode = enabled;
    localStorage.setItem('billboard_mode', enabled.toString());
  }

  public isBillboard(): boolean {
    return this.isBillboardMode;
  }

  private async refreshSession() {
    if (this.isDestroyed) return false;
    
    // Throttle refresh attempts
    const now = Date.now();
    if (now - this.lastRefreshTime < 10000) { // Don't refresh more than once every 10 seconds
      return true;
    }
    
    this.lastRefreshTime = now;

    try {
      if (import.meta.env.DEV) {
        console.log('[AuthStateManager] Refreshing session');
      }
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('[AuthStateManager] Session refresh error:', error);
        return false;
      }

      if (session) {
        this.retryAttempts = 0; // Reset retry attempts on success
        if (import.meta.env.DEV) {
          console.log('[AuthStateManager] Session refreshed successfully');
        }
        return true;
      }

      return false;
    } catch (error) {
      console.error('[AuthStateManager] Error refreshing session:', error);
      return false;
    }
  }

  public async startAutoRefresh() {
    // Simplified - just update state, let supabase handle refresh
    this.currentState = 'active';
  }

  public stopAutoRefresh() {
    // Simplified - let supabase handle its own refresh lifecycle
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }

  public cleanup() {
    console.log('[AuthStateManager] Cleaning up');
    this.stopAutoRefresh();
    this.isDestroyed = true;
  }

  public getCurrentState(): SessionState {
    return this.currentState;
  }
}
